import type {
  AICaptionMediaItem,
  ApprovalRequest,
  ApprovalRequestStatusUpdate,
  BlueskyScheduledPost,
  BulkScheduledPostRequest,
  BulkScheduledPostResponse,
  Event,
  FacebookLinkPreview,
  FacebookScheduledPost,
  GenerateAICaptionsResponse,
  GetLinkedinFollowersSearchParams,
  GetLinkedinMemberDetailsParams,
  InstagramScheduledPost,
  LinkedInScheduledPost,
  ListRSSFeedsResponse,
  MemberProfilesResponse,
  PinterestScheduledPost,
  PostReorderRequest,
  RecommendedPublishTimesResponse,
  ReorderResponse,
  RSSFeedRequest,
  RSSFeedResponse,
  RSSFeedValidationRequest,
  RSSFeedValidationResponse,
  SnapchatScheduledPost,
  ThreadsScheduledPost,
  TikTokMusicInfo,
  TikTokScheduledPost,
  TwitterScheduledPost,
  UpdateFacebookRequest,
  UpdateInstagramRequest,
  UpdateLinkedInRequest,
  UpdatePinterestRequest,
  UpdateRSSFeedRequest,
  UpdateSnapchatRequest,
  UpdateTikTokRequest,
  UpdateYouTubeRequest,
  YouTubeScheduledPost,
} from '@dashhudson/api-client/scheduler/model';

import {
  getDeleteBlueskyScheduledPostsPostIdUrl,
  getGetShareableCalendarLinksShareableCalendarLinkIdUrl,
  getPostAiCaptionsUrl,
  getGetEventsUrl,
  getGetShareableCalendarLinksUrl,
  getPostEventsUrl,
  getPostScheduledPostsBulkUrl,
  getPostShareableCalendarLinksUrl,
  getDeleteEventsEventIdUrl,
  getDeleteFacebookScheduledPostsPostIdUrl,
  getDeleteInstagramScheduledPostsPostIdUrl,
  getDeleteLinkedinScheduledPostsPostIdUrl,
  getDeletePinterestScheduledPostsPostIdUrl,
  getDeleteRssFeedsRssFeedIdUrl,
  getDeleteShareableCalendarLinksShareableCalendarLinkIdUrl,
  getDeleteSnapchatScheduledPostsPostIdUrl,
  getDeleteThreadsScheduledPostsPostIdUrl,
  getDeleteTiktokScheduledPostsPostIdUrl,
  getDeleteTwitterScheduledPostsPostIdUrl,
  getDeleteYoutubeScheduledPostsPostIdUrl,
  getGetBlueskyScheduledPostsPostIdUrl,
  getGetBlueskyScheduledPostsUrl,
  getGetFacebookScheduledPostsPostIdUrl,
  getGetFacebookScheduledPostsUrl,
  getGetInstagramScheduledPostsPostIdUrl,
  getGetInstagramScheduledPostsUrl,
  getGetLinkedinScheduledPostsPostIdUrl,
  getGetLinkedinScheduledPostsUrl,
  getGetPinterestScheduledPostsPostIdUrl,
  getGetPinterestScheduledPostsUrl,
  getGetRecommendedPublishTimesUrl,
  getGetRssFeedsRssFeedIdUrl,
  getGetSnapchatScheduledPostsPostIdUrl,
  getGetSnapchatScheduledPostsUrl,
  getGetThreadsScheduledPostsPostIdUrl,
  getGetThreadsScheduledPostsUrl,
  getGetTiktokScheduledPostsPostIdUrl,
  getGetTiktokScheduledPostsUrl,
  getGetTwitterScheduledPostsPostIdUrl,
  getGetTwitterScheduledPostsUrl,
  getGetYoutubeScheduledPostsPostIdUrl,
  getGetYoutubeScheduledPostsUrl,
  getPatchApprovalRequestsApprovalRequestIdUrl,
  getPatchBlueskyScheduledPostsPostIdUrl,
  getPatchEventsEventIdUrl,
  getPatchFacebookScheduledPostsPostIdUrl,
  getPatchInstagramScheduledPostsPostIdUrl,
  getPatchLinkedinScheduledPostsPostIdUrl,
  getPatchPinterestScheduledPostsPostIdUrl,
  getPatchRssFeedsRssFeedIdUrl,
  getPatchShareableCalendarLinksShareableCalendarLinkIdUrl,
  getPatchSnapchatScheduledPostsPostIdUrl,
  getPatchThreadsScheduledPostsPostIdUrl,
  getPatchTiktokScheduledPostsPostIdUrl,
  getPatchTwitterScheduledPostsPostIdUrl,
  getPatchYoutubeScheduledPostsPostIdUrl,
  getPostBlueskyScheduledPostsBatchUrl,
  getPostBlueskyScheduledPostsPostIdReorderUrl,
  getPostBlueskyScheduledPostsUrl,
  getPostFacebookScheduledPostsBatchUrl,
  getPostFacebookScheduledPostsPostIdReorderUrl,
  getPostFacebookScheduledPostsUrl,
  getPostInstagramScheduledPostsBatchUrl,
  getPostInstagramScheduledPostsPostIdReorderUrl,
  getPostInstagramScheduledPostsUrl,
  getPostLinkedinScheduledPostsBatchUrl,
  getPostLinkedinScheduledPostsPostIdReorderUrl,
  getPostLinkedinScheduledPostsUrl,
  getPostPinterestScheduledPostsBatchUrl,
  getPostPinterestScheduledPostsPostIdReorderUrl,
  getPostPinterestScheduledPostsUrl,
  getPostRssFeedsUrl,
  getPostRssFeedsValidateUrl,
  getPostSnapchatScheduledPostsBatchUrl,
  getPostSnapchatScheduledPostsPostIdReorderUrl,
  getPostSnapchatScheduledPostsUrl,
  getPostThreadsScheduledPostsBatchUrl,
  getPostThreadsScheduledPostsPostIdReorderUrl,
  getPostThreadsScheduledPostsUrl,
  getPostTiktokScheduledPostsBatchUrl,
  getPostTiktokScheduledPostsPostIdReorderUrl,
  getPostTiktokScheduledPostsUrl,
  getPostTwitterScheduledPostsBatchUrl,
  getPostTwitterScheduledPostsPostIdReorderUrl,
  getPostTwitterScheduledPostsUrl,
  getPostYoutubeScheduledPostsBatchUrl,
  getPostYoutubeScheduledPostsPostIdReorderUrl,
  getPostYoutubeScheduledPostsUrl,
} from '@dashhudson/api-client/scheduler/client';
import { decamelize } from 'humps';
import merge from 'lodash/merge';
import omit from 'lodash/omit';

import type { EXPIRY_OPTIONS } from '@/app/scheduler/components/shareable-links/shareableCalendarLink.utils';
import type { APPROVAL_STATUS, PLATFORMS } from '@/app/scheduler/constants';
import type { MediaItem } from '@/app/scheduler/types/media';
import type { HttpRequestConfig } from '@/types/http';

import { createDashHttpClient } from '@/apis/dash.http';
import { env } from '@/env';
import { useTokenStore } from '@/stores/token';
import { parseQuery } from '@/utils/query';

export const http = createDashHttpClient(env.schedulerApiUrl, {
  camelizeKeys: true,
  decamelizeIgnoreKeys: ['pinterestBoard'],
});

// The URL segment (`facebook`, `instagram`, …) keys both schema maps, so `platform`
// and `data` are checked as a pair instead of independently.
interface CreateSchemas {
  bluesky: BlueskyScheduledPost;
  facebook: FacebookScheduledPost;
  instagram: InstagramScheduledPost;
  linkedin: LinkedInScheduledPost;
  pinterest: PinterestScheduledPost;
  snapchat: SnapchatScheduledPost;
  threads: ThreadsScheduledPost;
  tiktok: TikTokScheduledPost;
  twitter: TwitterScheduledPost;
  youtube: YouTubeScheduledPost;
}

// Bluesky/Threads/Twitter generate no Update*Request; they patch with the post shape.
interface PatchSchemas {
  bluesky: BlueskyScheduledPost;
  facebook: UpdateFacebookRequest;
  instagram: UpdateInstagramRequest;
  linkedin: UpdateLinkedInRequest;
  pinterest: UpdatePinterestRequest;
  snapchat: UpdateSnapchatRequest;
  threads: ThreadsScheduledPost;
  tiktok: UpdateTikTokRequest;
  twitter: TwitterScheduledPost;
  youtube: UpdateYouTubeRequest;
}

type Platform = keyof CreateSchemas;

interface ScheduledPostUrls {
  list: () => string;
  detail: (postId: number) => string;
  create: () => string;
  batch: () => string;
  update: (postId: number) => string;
  remove: (postId: number) => string;
  reorder: (postId: number) => string;
}

// The spec generates a separate route per platform, so `/${platform}/scheduled_posts` can only
// be built from a table. `satisfies` is what makes it exhaustive: a platform the spec drops, or
// one added to `CreateSchemas` without routes, reds `type-check` instead of 404ing.
const SCHEDULED_POST_URLS = {
  bluesky: {
    list: getGetBlueskyScheduledPostsUrl,
    detail: getGetBlueskyScheduledPostsPostIdUrl,
    create: getPostBlueskyScheduledPostsUrl,
    batch: getPostBlueskyScheduledPostsBatchUrl,
    update: getPatchBlueskyScheduledPostsPostIdUrl,
    remove: getDeleteBlueskyScheduledPostsPostIdUrl,
    reorder: getPostBlueskyScheduledPostsPostIdReorderUrl,
  },
  facebook: {
    list: getGetFacebookScheduledPostsUrl,
    detail: getGetFacebookScheduledPostsPostIdUrl,
    create: getPostFacebookScheduledPostsUrl,
    batch: getPostFacebookScheduledPostsBatchUrl,
    update: getPatchFacebookScheduledPostsPostIdUrl,
    remove: getDeleteFacebookScheduledPostsPostIdUrl,
    reorder: getPostFacebookScheduledPostsPostIdReorderUrl,
  },
  instagram: {
    list: getGetInstagramScheduledPostsUrl,
    detail: getGetInstagramScheduledPostsPostIdUrl,
    create: getPostInstagramScheduledPostsUrl,
    batch: getPostInstagramScheduledPostsBatchUrl,
    update: getPatchInstagramScheduledPostsPostIdUrl,
    remove: getDeleteInstagramScheduledPostsPostIdUrl,
    reorder: getPostInstagramScheduledPostsPostIdReorderUrl,
  },
  linkedin: {
    list: getGetLinkedinScheduledPostsUrl,
    detail: getGetLinkedinScheduledPostsPostIdUrl,
    create: getPostLinkedinScheduledPostsUrl,
    batch: getPostLinkedinScheduledPostsBatchUrl,
    update: getPatchLinkedinScheduledPostsPostIdUrl,
    remove: getDeleteLinkedinScheduledPostsPostIdUrl,
    reorder: getPostLinkedinScheduledPostsPostIdReorderUrl,
  },
  pinterest: {
    list: getGetPinterestScheduledPostsUrl,
    detail: getGetPinterestScheduledPostsPostIdUrl,
    create: getPostPinterestScheduledPostsUrl,
    batch: getPostPinterestScheduledPostsBatchUrl,
    update: getPatchPinterestScheduledPostsPostIdUrl,
    remove: getDeletePinterestScheduledPostsPostIdUrl,
    reorder: getPostPinterestScheduledPostsPostIdReorderUrl,
  },
  snapchat: {
    list: getGetSnapchatScheduledPostsUrl,
    detail: getGetSnapchatScheduledPostsPostIdUrl,
    create: getPostSnapchatScheduledPostsUrl,
    batch: getPostSnapchatScheduledPostsBatchUrl,
    update: getPatchSnapchatScheduledPostsPostIdUrl,
    remove: getDeleteSnapchatScheduledPostsPostIdUrl,
    reorder: getPostSnapchatScheduledPostsPostIdReorderUrl,
  },
  threads: {
    list: getGetThreadsScheduledPostsUrl,
    detail: getGetThreadsScheduledPostsPostIdUrl,
    create: getPostThreadsScheduledPostsUrl,
    batch: getPostThreadsScheduledPostsBatchUrl,
    update: getPatchThreadsScheduledPostsPostIdUrl,
    remove: getDeleteThreadsScheduledPostsPostIdUrl,
    reorder: getPostThreadsScheduledPostsPostIdReorderUrl,
  },
  tiktok: {
    list: getGetTiktokScheduledPostsUrl,
    detail: getGetTiktokScheduledPostsPostIdUrl,
    create: getPostTiktokScheduledPostsUrl,
    batch: getPostTiktokScheduledPostsBatchUrl,
    update: getPatchTiktokScheduledPostsPostIdUrl,
    remove: getDeleteTiktokScheduledPostsPostIdUrl,
    reorder: getPostTiktokScheduledPostsPostIdReorderUrl,
  },
  twitter: {
    list: getGetTwitterScheduledPostsUrl,
    detail: getGetTwitterScheduledPostsPostIdUrl,
    create: getPostTwitterScheduledPostsUrl,
    batch: getPostTwitterScheduledPostsBatchUrl,
    update: getPatchTwitterScheduledPostsPostIdUrl,
    remove: getDeleteTwitterScheduledPostsPostIdUrl,
    reorder: getPostTwitterScheduledPostsPostIdReorderUrl,
  },
  youtube: {
    list: getGetYoutubeScheduledPostsUrl,
    detail: getGetYoutubeScheduledPostsPostIdUrl,
    create: getPostYoutubeScheduledPostsUrl,
    batch: getPostYoutubeScheduledPostsBatchUrl,
    update: getPatchYoutubeScheduledPostsPostIdUrl,
    remove: getDeleteYoutubeScheduledPostsPostIdUrl,
    reorder: getPostYoutubeScheduledPostsPostIdReorderUrl,
  },
} satisfies Record<Platform, ScheduledPostUrls>;

type IsEqual<X, Y> =
  // oxlint-disable-next-line typescript/no-unnecessary-type-parameters -- standard exact-equality trick
  (<G>() => G extends X ? 1 : 2) extends <G>() => G extends Y ? 1 : 2 ? true : false;

// The spec marks server-assigned fields `readonly`, and the backend `EXCLUDE`s them on
// the way in — so read-only is exactly the line between a response and a request body.
type ReadonlyKeys<T> = {
  [K in keyof T]-?: IsEqual<{ [Q in K]: T[K] }, { -readonly [Q in K]: T[K] }> extends true
    ? never
    : K;
}[keyof T];
// Distributive on purpose: bare `Omit` over a union collapses to the keys every member
// shares, which would reject `caption` and `text` alike on the unparameterised form.
type WriteBody<T> = T extends unknown ? Omit<T, ReadonlyKeys<T>> : never;

// Named to avoid clashing with the loose hand-rolled `ScheduledPost` in
// `@/app/scheduler/types/post.ts`; this is the spec-generated wire union.
export type PlatformScheduledPost = CreateSchemas[Platform];

// Unparameterised these stay the full union, which is all an untyped JS caller can
// offer; a TS caller passing a literal platform gets that platform's schema alone.
export type CreatePostRequest<P extends Platform = Platform> = WriteBody<CreateSchemas[P]>;
export type UpdatePostRequest<P extends Platform = Platform> = WriteBody<PatchSchemas[P]>;

export interface FetchPostsParams {
  platform: Platform;
  brandId?: number;
  brandIds?: number[] | string;
  start?: string;
  end?: string;
  sort?: string;
  status?: string[];
  limit?: number;
  hasScheduledTime?: boolean;
}

// Response is the per-platform List*PostsResponse shape (only a subset of
// platforms have a typed list response in the spec), so we leave the response
// loose and let callers narrow.
export const fetchPosts = (
  {
    platform,
    brandId,
    brandIds,
    start,
    end,
    sort,
    status,
    limit,
    hasScheduledTime,
  }: FetchPostsParams,
  config: HttpRequestConfig = {},
) =>
  http.get(SCHEDULED_POST_URLS[platform].list(), {
    ...config,
    params: {
      brandId,
      brandIds,
      start,
      end,
      sort,
      status: status?.join?.(','),
      limit,
      hasScheduledTime,
      mediaV2: true,
    },
  });

// The metadata endpoint requires the presented dash_token as a query param (on
// top of the session Authorization header) so the backend can bind the token to
// this specific link; a mismatched or disabled link responds 404, an expired or
// invalid token 401.
export const getSharedCalendarLink = (
  { sharedLinkId }: { sharedLinkId: string },
  config: HttpRequestConfig = {},
) =>
  http.get<SharedCalendarLinkResponse>(
    getGetShareableCalendarLinksShareableCalendarLinkIdUrl(Number(sharedLinkId)),
    {
      ...config,
      params: { dash_token: useTokenStore().token },
    },
  );

// Twitter replies are serialized through their own restricted schema
// (TWITTER_REPLY_SHARED_FIELDS in scheduler-backend) with resolved media
// attached per reply.
interface SharedCalendarTwitterReply {
  id?: number;
  status?: string;
  livePostUrl?: string | null;
  mediaIds?: number[];
  media?: MediaItem[];
  tweetStatus?: string;
  threadOrder?: number;
}

// Per-platform content fields serialized by the restricted shared schemas
// (PLATFORM_SHARED_FIELDS in scheduler-backend); each platform returns only
// its own subset.
interface SharedCalendarPostContentFields {
  autoPublish?: boolean;
  brandedContentType?: string;
  caption?: string;
  coverImageMediaId?: number | null;
  description?: string;
  link?: string | null;
  linkPreview?: FacebookLinkPreview | null;
  message?: string;
  meta?: { title?: string; thumbOffset?: number | null } | null;
  musicInfo?: TikTokMusicInfo | null;
  note?: string;
  pinterestBoard?: Record<string, unknown> | null;
  postType?: string;
  replies?: SharedCalendarTwitterReply[];
  subtitleContent?: string | null;
  text?: string;
  thumbnailOffset?: number | null;
  thumbnailUrl?: string | null;
  thumbOffset?: number | null;
  title?: string;
  tweetStatus?: string;
  videoTitle?: string;
}

// The shared posts endpoint serializes each post through a restricted
// per-platform schema (COMMON_SHARED_FIELDS in scheduler-backend) and injects
// `platform`, `approvalSummary`, resolved `media` and, for reels, a resolved
// `coverImageMedia` — a narrow projection of PlatformScheduledPost, never the
// full editor shape.
export type SharedCalendarPost = Pick<
  PlatformScheduledPost,
  'id' | 'brandId' | 'timestamp' | 'status' | 'livePostUrl' | 'mediaIds'
> &
  SharedCalendarPostContentFields & {
    platform: string;
    approvalStatus?: string;
    approvalSummary?: { approved: number; pending: number; unassigned: number };
    media?: MediaItem[];
    coverImageMedia?: MediaItem | null;
  };

// The recipient's session share token (sent as `Authorization: Bearer
// share-<token>`) is what binds the request to a link, so the posts endpoint
// takes no link id — only the date window.
export const fetchSharedCalendarPosts = (
  { start, end }: { start?: string; end?: string },
  config: HttpRequestConfig = {},
) =>
  http.get<{ data: SharedCalendarPost[] }>('/shareable_calendar_links/posts', {
    ...config,
    params: { start, end },
  });

export const fetchPost = (
  { platform, id }: { platform: Platform; id: number },
  config: HttpRequestConfig = {},
) =>
  http.get<PlatformScheduledPost>(SCHEDULED_POST_URLS[platform].detail(id), {
    ...config,
    params: {
      mediaV2: true,
    },
  });

// Fails the build unless `T` is `never` — the shape of every drift check below.
type AssertNever<T extends never> = T;
type Mismatch<A, B> = Exclude<A, B> | Exclude<B, A>;
type PlatformsDeclaring<M, K extends PropertyKey> = {
  [P in keyof M]: K extends keyof WriteBody<M[P]> ? P : never;
}[keyof M];
// `keyof` a union yields the shared keys only; map first to get every platform's keys.
type AnyWritableKey<M> = { [P in keyof M]: keyof WriteBody<M[P]> }[keyof M];
// Per-member keys: `keyof` a union collapses to the shared ones, which is the failure
// the last entry of `SchemaDriftChecks` guards against.
type MemberKeys<T> = T extends unknown ? keyof T : never;

// Frontend-only drawer state. No write schema declares these in either direction, so
// there is nothing to derive them from — the assertion below is what pins them.
const FRONTEND_ONLY_KEYS = [
  'originalContent',
  'mentionedUsernames',
  'mentionWarningType',
  'galleryTypes',
  'shortenLink',
  'customUtms',
  'thumbnailSource',
] as const;

// Read-only echoes the drawer carries back from a fetch.
const READ_ONLY_ECHO_KEYS = ['media', 'location', 'coverImageMedia'] as const;

const PLATFORMS_WITH_POST_TYPE = ['facebook', 'instagram', 'snapchat'] as const;
const PATCH_PLATFORMS_WITH_POST_TYPE = ['facebook', 'snapchat'] as const;
const PATCH_PLATFORMS_WITH_BRAND_ID = [
  'bluesky',
  'linkedin',
  'snapchat',
  'threads',
  'twitter',
  'youtube',
] as const;

/**
 * Each runtime list above is a claim about the generated schemas. Every entry here must
 * stay `never`, so a spec change that invalidates a claim reds `type-check` rather than
 * silently dropping a key the backend has started accepting.
 * @public
 */
export type SchemaDriftChecks = [
  AssertNever<
    Extract<
      (typeof FRONTEND_ONLY_KEYS | typeof READ_ONLY_ECHO_KEYS)[number],
      AnyWritableKey<CreateSchemas> | AnyWritableKey<PatchSchemas>
    >
  >,
  AssertNever<
    Mismatch<
      PlatformsDeclaring<CreateSchemas, 'postType'>,
      (typeof PLATFORMS_WITH_POST_TYPE)[number]
    >
  >,
  AssertNever<
    Mismatch<
      PlatformsDeclaring<PatchSchemas, 'postType'>,
      (typeof PATCH_PLATFORMS_WITH_POST_TYPE)[number]
    >
  >,
  AssertNever<
    Mismatch<
      PlatformsDeclaring<PatchSchemas, 'brandId'>,
      (typeof PATCH_PLATFORMS_WITH_BRAND_ID)[number]
    >
  >,
  // `WriteBody` must stay distributive: a non-distributive `Omit` silently narrows the
  // unparameterised form to the keys all ten platforms share, rejecting both of these.
  AssertNever<Exclude<'caption' | 'text', MemberKeys<CreatePostRequest>>>,
];

function toWritablePost<T extends object>(platform: Platform, data: T, { patch = false } = {}) {
  const keepsPostType: readonly string[] = patch
    ? PATCH_PLATFORMS_WITH_POST_TYPE
    : PLATFORMS_WITH_POST_TYPE;
  const dropped: string[] = [...FRONTEND_ONLY_KEYS, ...READ_ONLY_ECHO_KEYS];
  if (!keepsPostType.includes(platform)) dropped.push('postType');
  if (patch && !(PATCH_PLATFORMS_WITH_BRAND_ID as readonly string[]).includes(platform)) {
    dropped.push('brandId');
  }
  // Callers hand over either spelling: drawers decamelize before the store, specs seed wire keys.
  return omit(
    data,
    dropped.flatMap((key) => [key, decamelize(key)]),
  );
}

export const createPost = <P extends Platform>(
  { platform, data }: { platform: P; data: CreatePostRequest<P> },
  config: HttpRequestConfig = {},
) =>
  http.post(SCHEDULED_POST_URLS[platform].create(), toWritablePost(platform, data), {
    ...config,
    params: {
      mediaV2: true,
    },
  });

export const batchCreatePost = <P extends Platform>(
  { platform, data }: { platform: P; data: CreatePostRequest<P> },
  config: HttpRequestConfig = {},
) =>
  http.post<PlatformScheduledPost[]>(
    SCHEDULED_POST_URLS[platform].batch(),
    toWritablePost(platform, data),
    {
      ...config,
      params: {
        mediaV2: true,
      },
    },
  );

export const fetchPostGroup = (
  { postGroupId, brandId }: { postGroupId: string; brandId: number },
  config: HttpRequestConfig = {},
) =>
  http.get<{ data: PlatformScheduledPost[] }>(`/scheduled_posts/groups/${postGroupId}`, {
    ...config,
    params: {
      brandId,
      resolveReferences: true,
      mediaV2: true,
    },
  });

export const createBulkPosts = (
  {
    posts,
    bulkSessionId,
    brandId,
  }: { posts: BulkScheduledPostRequest['posts']; bulkSessionId: string; brandId: number },
  config: HttpRequestConfig = {},
) =>
  http.post<BulkScheduledPostResponse>(
    getPostScheduledPostsBulkUrl(),
    { posts, bulkSessionId, brandId },
    config,
  );

export const updatePost = <P extends Platform>(
  { platform, id, data }: { platform: P; id: number; data: UpdatePostRequest<P> },
  config: HttpRequestConfig = {},
) =>
  http.patch(
    SCHEDULED_POST_URLS[platform].update(id),
    toWritablePost(platform, data, { patch: true }),
    {
      ...config,
      params: {
        mediaV2: true,
      },
    },
  );

export const deletePost = (
  { platform, id }: { platform: Platform; id: number },
  config: HttpRequestConfig = {},
) =>
  http.delete<void>(SCHEDULED_POST_URLS[platform].remove(id), {
    ...config,
    params: {
      mediaV2: true,
    },
  });

export const reorderPost = (
  { platform, id, brandId, after }: { platform: Platform; id: number } & PostReorderRequest,
  config: HttpRequestConfig = {},
) =>
  http.post<ReorderResponse>(
    SCHEDULED_POST_URLS[platform].reorder(id),
    { after, brandId },
    {
      ...config,
      params: {
        mediaV2: true,
      },
    },
  );

export const fetchEvents = ({ brandId }: { brandId: number }, config: HttpRequestConfig = {}) =>
  http.get<Event[]>(getGetEventsUrl(), {
    ...config,
    params: { brandId },
  });

export const createEvent = (
  { brandId, title, startDate, endDate, description, color }: Event,
  config: HttpRequestConfig = {},
) =>
  http.post<Event>(
    getPostEventsUrl(),
    {
      brandId,
      title,
      startDate,
      endDate,
      description,
      color,
    },
    config,
  );

// The spec leaves `Event.id` optional (it is server-assigned), but a patch needs one — the
// generated builder is what turned `/events/undefined` into a compile error.
export const updateEvent = (
  { id, brandId, title, startDate, endDate, description, color }: Event & { id: number },
  config: HttpRequestConfig = {},
) =>
  http.patch<Event>(
    getPatchEventsEventIdUrl(id),
    {
      brandId,
      title,
      startDate,
      endDate,
      description,
      color,
    },
    config,
  );

export const deleteEvent = ({ id }: { id: number }, config: HttpRequestConfig = {}) =>
  http.delete<void>(getDeleteEventsEventIdUrl(id), config);

export const updateApprovalRequest = (
  {
    approvalRequestId,
    approvalStatus,
  }: { approvalRequestId: number; approvalStatus: ApprovalRequestStatusUpdate['approvalStatus'] },
  config: HttpRequestConfig = {},
) =>
  http.patch<ApprovalRequest>(
    getPatchApprovalRequestsApprovalRequestIdUrl(approvalRequestId),
    { approval_status: approvalStatus },
    config,
  );

export async function getRecommendedPublishTimes(
  { brandId, platform, timezone }: { brandId: number; platform: string; timezone: string },
  config: HttpRequestConfig = {},
) {
  return http.get<RecommendedPublishTimesResponse>(getGetRecommendedPublishTimesUrl(), {
    ...config,
    params: { brandId, platform_type: platform.toUpperCase(), timezone },
  });
}

/**
 * Validates an RSS feed URL with brand and name context.
 */
export async function validateRssFeed(
  { url, brandId, name, rssFeedId }: RSSFeedValidationRequest,
  config: HttpRequestConfig = {},
) {
  const data: RSSFeedValidationRequest = { url, brandId, name };
  if (rssFeedId) {
    data.rssFeedId = rssFeedId;
  }
  return http.post<RSSFeedValidationResponse>(getPostRssFeedsValidateUrl(), data, config);
}

/**
 * Retrieves a list of RSS feeds for specified brand IDs. `url`, when provided,
 * is parsed for additional query params (used to follow pagination cursors).
 */
export async function getRssFeedsList(
  {
    brandIds,
    url,
    limit,
    orderByBrandIds,
  }: {
    brandIds: number[] | number | string;
    url?: string;
    limit?: number;
    orderByBrandIds?: boolean;
  },
  config: HttpRequestConfig = {},
) {
  const params: Record<string, unknown> = {};
  if (url) {
    merge(params, parseQuery(url));
  }
  return http.get<ListRSSFeedsResponse>(`/rss_feeds`, {
    ...config,
    params: {
      ...params,
      brandIds: Array.isArray(brandIds) ? brandIds.join(',') : brandIds,
      limit,
      orderByBrandIds,
    },
  });
}

/**
 * Retrieves a single RSS feed by ID.
 */
export async function getRssFeed(id: number, config: HttpRequestConfig = {}) {
  return http.get<RSSFeedResponse>(getGetRssFeedsRssFeedIdUrl(id), config);
}

/**
 * Creates a new RSS feed with channel configurations.
 */
export async function createRssFeed(
  { brandId, url, name, frequency, status, enabled, channelConfigs, sampleItem }: RSSFeedRequest,
  config: HttpRequestConfig = {},
) {
  const data: RSSFeedRequest = {
    brandId,
    url,
    name,
    frequency,
    status,
    enabled,
    channelConfigs,
    sampleItem,
  };

  return http.post<RSSFeedResponse>(getPostRssFeedsUrl(), data, config);
}

/** Updates an existing RSS feed; the body is exactly `UpdateRSSFeedRequest`. */
export async function updateRssFeed(
  {
    id,
    url,
    name,
    frequency,
    status,
    enabled,
    channelConfigs,
    sampleItem,
  }: UpdateRSSFeedRequest & { id: number },
  config: HttpRequestConfig = {},
) {
  const data: UpdateRSSFeedRequest = {
    url,
    name,
    frequency,
    status,
    enabled,
    channelConfigs,
    sampleItem,
  };

  return http.patch<RSSFeedResponse>(getPatchRssFeedsRssFeedIdUrl(id), data, config);
}

/**
 * Deletes an RSS feed by ID.
 */
export async function deleteRssFeed(id: number, config: HttpRequestConfig = {}) {
  return http.delete<void>(getDeleteRssFeedsRssFeedIdUrl(id), config);
}

/**
 * Search for LinkedIn organization followers for @mentions.
 */
export async function searchLinkedInFollowers(
  { brandId, keyword }: GetLinkedinFollowersSearchParams,
  config: HttpRequestConfig = {},
) {
  return http.get(`/linkedin/followers/search`, {
    params: { brand_id: brandId, keyword },
    ...config,
  });
}

/**
 * Get LinkedIn member details (avatar, name, headline) by URNs.
 */
export async function getLinkedInMemberDetails(
  { brandId, personUrns }: GetLinkedinMemberDetailsParams,
  config: HttpRequestConfig = {},
) {
  return http.get<MemberProfilesResponse>(`/linkedin/member_details`, {
    params: { brand_id: brandId, person_urns: personUrns },
    paramsSerializer: {
      indexes: null, // serializes as person_urns=x&person_urns=y (no brackets)
    },
    ...config,
  });
}

/**
 * Generate AI captions for bulk media items. `platform` and `mediaType` are
 * loosened to `string` so callers can forward the runtime platform/media
 * strings they hold — the spec types them as enums but the wire contract
 * accepts the raw string values.
 */
export interface GenerateAICaptionsParams {
  brandId: number;
  platform: string;
  mediaItems: Array<Omit<AICaptionMediaItem, 'mediaType'> & { mediaType: string }>;
}

export async function generateAICaptions(
  { brandId, platform, mediaItems }: GenerateAICaptionsParams,
  config: HttpRequestConfig = {},
) {
  return http.post<GenerateAICaptionsResponse>(
    getPostAiCaptionsUrl(),
    { brandId, platform, mediaItems },
    { timeout: 30000, ...config },
  );
}

// ---------------------------------------------------------------------------
// Shareable calendar links (`/shareable_calendar_links`).
// ---------------------------------------------------------------------------

/**
 * The platforms a shareable link can filter by. Deliberately platform-level only (no Instagram
 * sub-channels): a link with `INSTAGRAM` shares all IG post types; the recipient view offers its
 * own post-type display filters.
 */
export type ShareableCalendarLinkChannel = keyof typeof PLATFORMS;

export type ShareableCalendarLinkApprovalStatus = keyof typeof APPROVAL_STATUS;

/**
 * Expiry presets accepted on create/renew; the backend computes the actual expiry date. The
 * values are defined once, on `EXPIRY_OPTIONS`, and must mirror the backend's expiry preset enum.
 */
export type ShareableCalendarLinkExpiryPreset = (typeof EXPIRY_OPTIONS)[number]['value'];

export interface ShareableCalendarLinkFilters {
  brandIds: number[];
  channels: ShareableCalendarLinkChannel[];
  approvalStatuses: ShareableCalendarLinkApprovalStatus[];
  showDrafts: boolean;
}

export interface ShareableCalendarLink {
  id: number;
  name: string;
  enabled: boolean;
  validUntil: string;
  url: string;
  filters: ShareableCalendarLinkFilters;
  createdByUserId: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateShareableCalendarLinkRequest {
  name: string;
  validUntil: ShareableCalendarLinkExpiryPreset;
  filters: ShareableCalendarLinkFilters;
}

/**
 * PATCH body — send only the fields being changed. `validUntil` renews the link and must extend
 * the current expiry (400 otherwise).
 */
export interface UpdateShareableCalendarLinkRequest {
  name?: string;
  enabled?: boolean;
  validUntil?: ShareableCalendarLinkExpiryPreset;
  filters?: ShareableCalendarLinkFilters;
}

// Recipient (public share-token) read model. Kept separate from the owner-facing
// ShareableCalendarLink above: the public metadata endpoint returns only these
// fields, and its channels arrive as lowercase platform values rather than the
// uppercase CHANNEL_FILTER_OPTIONS keys the sender types use.
interface SharedCalendarLinkFiltersResponse {
  brandIds: number[];
  channels: string[];
  approvalStatuses: string[];
  showDrafts: boolean;
}

export interface SharedCalendarLinkResponse {
  name: string;
  filters: SharedCalendarLinkFiltersResponse;
  validUntil: string;
}

// The API's channel values are the backend's lowercase `PlatformType` enum; the app uses the
// scheduler's uppercase channel filter keys. Map the casing at this boundary.
type ApiShareableCalendarLinkFilters = Omit<ShareableCalendarLinkFilters, 'channels'> & {
  channels: string[];
};

type ApiShareableCalendarLink = Omit<ShareableCalendarLink, 'filters'> & {
  filters: ApiShareableCalendarLinkFilters;
};

function toApiFilters(filters: ShareableCalendarLinkFilters): ApiShareableCalendarLinkFilters {
  return { ...filters, channels: filters.channels.map((channel) => channel.toLowerCase()) };
}

function fromApiLink(link: ApiShareableCalendarLink): ShareableCalendarLink {
  return {
    ...link,
    filters: {
      ...link.filters,
      // The API's channel values are the lowercase PLATFORMS values, so uppercasing yields keys.
      channels: link.filters.channels.map(
        (channel) => channel.toUpperCase() as ShareableCalendarLinkChannel,
      ),
    },
  };
}

export const getShareableCalendarLinks = async (config: HttpRequestConfig = {}) => {
  const response = await http.get<{ data: ApiShareableCalendarLink[] }>(
    getGetShareableCalendarLinksUrl(),
    { ...config },
  );
  return { ...response, data: { data: response.data.data.map(fromApiLink) } };
};

export const createShareableCalendarLink = async (
  data: CreateShareableCalendarLinkRequest,
  config: HttpRequestConfig = {},
) => {
  const response = await http.post<ApiShareableCalendarLink>(
    getPostShareableCalendarLinksUrl(),
    { ...data, filters: toApiFilters(data.filters) },
    { ...config },
  );
  return { ...response, data: fromApiLink(response.data) };
};

export const updateShareableCalendarLink = async (
  { shareableLinkId, ...data }: { shareableLinkId: number } & UpdateShareableCalendarLinkRequest,
  config: HttpRequestConfig = {},
) => {
  const response = await http.patch<ApiShareableCalendarLink>(
    getPatchShareableCalendarLinksShareableCalendarLinkIdUrl(shareableLinkId),
    data.filters ? { ...data, filters: toApiFilters(data.filters) } : data,
    { ...config },
  );
  return { ...response, data: fromApiLink(response.data) };
};

export const deleteShareableCalendarLink = (
  { shareableLinkId }: { shareableLinkId: number },
  config: HttpRequestConfig = {},
) =>
  http.delete<void>(getDeleteShareableCalendarLinksShareableCalendarLinkIdUrl(shareableLinkId), {
    ...config,
  });
